import * as React from 'react';
import { DragSourceMonitor, useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { FeatureFlags } from '../../../ts/FeatureFlags';

import { ExplorableItemDefinitions } from '../../../ts/models/ContentDefinitions';
import { getKeyForExplorableItem } from '../../../ts/models/ContentType';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IFavoritableItemInfo } from '../../../ts/models/resourceInfoParts';
import { ToasterService } from '../../../ts/services/ToasterService';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo } from '../../state/SpaceInfo';
import { Hover } from '../Components/Hover';
import { CardItemType, DraggableItemType } from '../DraggableItemTypes';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ContentCardProps } from './ContentCard';
import { ContentCardSelectCheckbox } from './ContentCardSelectCheckbox';
import { GenericCard } from './GenericCard';

export interface ContentCardWrapperStateProps {
  readonly urls: SpacesSiteUrls;
  readonly inWorkbookDatasourcesSubspace: boolean;
  readonly inDatasourceConnectionsSubspace: boolean;
  readonly inProjectSpace: boolean;
  readonly inRecentsSpace: boolean;
  readonly inFavoritesSpace: boolean;
  readonly spaceInfo: SpaceInfo;
}

export interface ContentCardWrapperDispatchProps {
  readonly addContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly getContentDetail: (content: IContentInfo) => void;
  readonly showLastAccessedAt?: boolean;
  readonly reorderItem: (fromIndex: number, toIndex: number, spaceInfo: SpaceInfo) => void;
  readonly callReorderItem: (fromIndex: number, toIndex: number, spaceInfo: SpaceInfo) => void;
}

export interface ContentCardWrapperOwnProps {
  readonly content: IContentInfo;
  readonly itemIndex?: number;
  readonly row?: number;
  readonly showActionMenu: boolean;
  readonly showLastAccessedAt?: boolean;
  readonly selected?: boolean;
  readonly toggleSelection?: (content: IContentInfo) => void;
  readonly forDragPreview: boolean;
  readonly dragInProgress: boolean;
  readonly setIsDragging: (isDragging: boolean) => void;
}

export type ContentCardWrapperProps = ContentCardWrapperOwnProps
  & ContentCardWrapperStateProps
  & ContentCardWrapperDispatchProps;

export const ContentCardWrapper = (props: ContentCardWrapperProps): JSX.Element => {
  const ref = React.useRef(null);
  const item: CardItemType = {
    type: DraggableItemType.Card,
    index: props.itemIndex,
    row: props.row ?? 0,
    content: props.content,
  };

  const [ { dragOpacity }, drag, preview ] = useDrag({
    item,
    begin: () => {
      props.setIsDragging(true);
      return {
        ...item,
        startIndex: item.index,
      };
    },
    collect: monitor => ({
      dragOpacity: monitor.isDragging() ? 0 : 1,
    }),
    end: (droppedItem: CardItemType, monitor: DragSourceMonitor) => {
      // Undoing un-favoriting an item from a toast doesn't work after reordering favorites because
      // the favorited item is in the wrong place. We also can't simply refresh the whole page when
      // undoing un-favoriting because the SOLR Favorites Core is slow and so we can't show the updates
      // fast enough. Instead, just remove the toasts after reordering.
      const toasterService = ServiceLocator.get<ToasterService>('ToasterService');
      toasterService.closeAllToastsNotMarkedAsKeep();

      const didCompleteDrop = monitor.didDrop();  // this is false if dropped outside drop target or ESC while dragging
      if (!didCompleteDrop) {
        if (droppedItem.startIndex < droppedItem.index) {
          props.reorderItem(droppedItem.index, droppedItem.startIndex - 1, props.spaceInfo);
        } else {
          props.reorderItem(droppedItem.index, droppedItem.startIndex, props.spaceInfo);
        }
        props.setIsDragging(false);
        return;
      }

      if (droppedItem.startIndex === droppedItem.index) {
        props.setIsDragging(false);
        return;
      }
      props.callReorderItem(droppedItem.startIndex, droppedItem.index - 1, props.spaceInfo);
      props.setIsDragging(false);
    }
  });

  const [ { dropOpacity }, drop ] = useDrop({
    accept: 'card',
    collect: monitor => ({
      dropOpacity: monitor.isOver() ? 0.5 : 1,
    }),
    hover: (draggedItem: CardItemType) => {
      if (!ref.current || draggedItem.index === item.index) return;

      const hoverRegion = targetCardRegion(draggedItem, item);

      if ((hoverRegion === 'right' || hoverRegion === 'bottom') && draggedItem.index !== item.index - 1) {
        props.reorderItem(draggedItem.index, item.index - 1, props.spaceInfo);
        draggedItem.index = item.index;
        draggedItem.row = item.row;
      } else if ((hoverRegion === 'left' || hoverRegion === 'top') && draggedItem.index !== item.index + 1) {
        props.reorderItem(draggedItem.index, item.index, props.spaceInfo);
        draggedItem.index = item.index;
        draggedItem.row = item.row;
      }
    },
    drop: (droppedItem: CardItemType) => { return droppedItem; }
  });

  React.useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  const [ isHovered, setIsHovered ] = React.useState(false);

  const testId = `${props.content.contentType}-${props.content.id}-card`;
  const hoveredOrSelected = props.selected || isHovered;

  // Make sure the checkbox is absolutely positioned with respect to the proper bounds
  const CardWrapperStyle: React.CSSProperties = {
    position: 'relative',
    opacity: Math.min(dragOpacity, dropOpacity),
  };

  // Only apply the drag and drop connectors in spaces that allow for reordering
  if (props.inFavoritesSpace && FeatureFlags.reorderFavoritesEnabled) {
    drag(drop(ref));
  }

  return (
    <div data-tb-test-id={testId} style={CardWrapperStyle} ref={ref}>
      <Hover groupName="content-card-select" onEnter={() => setIsHovered(true)} onLeave={() => setIsHovered(false)}>
        {hoveredOrSelected && <ContentCardSelectCheckbox {...props}/>}
        {React.cloneElement(React.Children.only(renderInnerCard(props, props.dragInProgress)), { hoveredOrSelected })}
      </Hover>
    </div>
  );
};

type CardRegion = 'left' | 'right' | 'top' | 'bottom';

function targetCardRegion(draggedCard: CardItemType, targetCard: CardItemType): CardRegion {
  if (draggedCard.row < targetCard.row) {
    return 'top';
  } else if (draggedCard.row > targetCard.row) {
    return 'bottom';
  } else if (draggedCard.index < targetCard.index) {
    return 'left';
  } else {
    return 'right';
  }
}

function renderInnerCard(wrapperProps: ContentCardWrapperProps, disableTooltip: boolean): JSX.Element {
  const cardProps: React.Attributes & ContentCardProps = {
    ...wrapperProps,
    key: getKeyForExplorableItem(wrapperProps.content),
    selected: wrapperProps.selected,
    disableTooltip,
  };

  const contentDefinition = ExplorableItemDefinitions[wrapperProps.content.contentType];
  const Card = contentDefinition ? contentDefinition.cardComponent : GenericCard;

  if (Card) {
    return <Card {...cardProps} />;
  } else {
    // No card component exists for some content types (e.g. flow output).
    return null;
  }
}
